<template>
  <v-container fluid>
    <!-- ============================================================ -->
    <!-- GREETING BANNER -->
    <!-- ============================================================ -->
    <v-card
      class="pa-5 mb-4 d-flex align-center justify-space-between flex-wrap"
      color="#0A7EA5"
      dark
    >
      <div>
        <div class="text-h6 font-weight-bold">
          👋 Incorporation &amp; Corporate Overview — สรุปสถานะงาน Docket
          ทั้งหมด
        </div>
        <div class="caption" style="opacity:.85">
          {{ todayLabel }} · ข้อมูล ณ วันนี้
        </div>
      </div>
      <div class="text-right">
        <div class="text-h4 font-weight-black">{{ pendingTotal }}</div>
        <div class="caption" style="opacity:.85">รายการรอดำเนินการ</div>
      </div>
    </v-card>

    <!-- ============================================================ -->
    <!-- LOADING -->
    <!-- ============================================================ -->
    <div v-if="loading" class="text-center pa-10">
      <v-progress-circular indeterminate color="primary" size="50" width="6">
      </v-progress-circular>
      <div class="mt-3 text-subtitle-1 primary--text">กำลังโหลดข้อมูล...</div>
    </div>

    <template v-else>
      <!-- ============================================================ -->
      <!-- STAT CARDS -->
      <!-- ============================================================ -->
      <v-row dense class="mb-2">
        <v-col v-for="card in statCards" :key="card.key" cols="6" sm="4" md="2">
          <v-card
            outlined
            class="pa-3 stat-card"
            :style="{ borderTop: `3px solid ${card.color}` }"
            @click="$router.push(card.route)"
          >
            <div class="caption font-weight-bold">
              {{ card.emoji }} {{ card.label }}
            </div>
            <div
              class="text-h4 font-weight-black"
              :style="{ color: card.color }"
            >
              {{ card.value }}
            </div>
            <div class="caption grey--text">{{ card.sub }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <!-- ========================================================== -->
        <!-- LEFT: URGENT -->
        <!-- ========================================================== -->
        <v-col cols="12" md="6">
          <div class="section-header mb-2">🚨 Urgent — ต้องดำเนินการด่วน</div>

          <div
            v-if="urgentDockets.length === 0"
            class="caption grey--text pa-4"
          >
            ไม่มีรายการที่ต้องดำเนินการด่วนในขณะนี้
          </div>

          <v-card
            v-for="d in urgentDockets"
            :key="d.id"
            outlined
            class="mb-2 pa-3 urgent-card"
            :class="urgentCardClass(d)"
            @click="goToDocket(d.id)"
          >
            <div class="d-flex align-start" style="gap:10px;">
              <div class="urgent-icon">{{ urgentIcon(d) }}</div>
              <div class="flex-grow-1">
                <div class="font-weight-bold" style="font-size:13px;">
                  {{ d.client_name
                  }}<span v-if="d.inquiry_topic"> — {{ d.inquiry_topic }}</span>
                </div>
                <div class="caption grey--text">
                  {{ d.doc_category }}
                </div>
                <div class="caption" style="font-size:10.5px;color:#9ca3af;">
                  Due: {{ formatDate(d.next_due_date) }}
                </div>
              </div>
              <div class="text-right" style="flex-shrink:0;">
                <v-chip x-small :color="urgentTagColor(d)" label>
                  {{ urgentTagLabel(d) }}
                </v-chip>
                <div
                  class="font-weight-black mt-1"
                  style="font-size:18px;"
                  :style="{ color: urgentDaysColor(d) }"
                >
                  {{ urgentDaysLabel(d) }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- ========================================================== -->
        <!-- RIGHT: SUMMARIES -->
        <!-- ========================================================== -->
        <v-col cols="12" md="6">
          <div class="section-header mb-2">
            📊 สรุปงานตาม Document Category
          </div>
          <v-card outlined class="mb-4">
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Document Category</th>
                    <th class="text-center">In Progress</th>
                    <th class="text-center">Complete</th>
                    <th class="text-center">Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="byDocCategory.length === 0">
                    <td colspan="4" class="text-center grey--text pa-4">
                      ไม่มีข้อมูล
                    </td>
                  </tr>
                  <tr v-for="row in byDocCategory" :key="row.doc_category">
                    <td>{{ row.doc_category }}</td>
                    <td class="text-center">{{ row.in_progress }}</td>
                    <td class="text-center">{{ row.complete }}</td>
                    <td class="text-center">
                      <v-chip
                        v-if="row.overdue > 0"
                        x-small
                        color="red lighten-4"
                        text-color="red darken-3"
                        label
                        >{{ row.overdue }}</v-chip
                      >
                      <span v-else class="grey--text">0</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <div class="section-header mb-2">
            🏛️ สรุปการเปลี่ยนแปลงตามหน่วยงาน
          </div>
          <v-card outlined class="mb-4 pa-3">
            <div v-if="byAgency.length === 0" class="caption grey--text pa-2">
              ไม่มีข้อมูล
            </div>
            <div
              v-for="row in byAgency"
              :key="row.agency_name"
              class="d-flex align-center justify-space-between agency-row"
            >
              <v-chip
                x-small
                color="amber lighten-4"
                text-color="orange darken-3"
                label
              >
                {{ row.agency_name }}
              </v-chip>
              <div class="d-flex align-center" style="gap:8px;">
                <div class="mini-bar">
                  <span :style="{ width: agencyBarWidth(row) + '%' }"></span>
                </div>
                <strong style="width:24px;text-align:right;">{{
                  row.total
                }}</strong>
              </div>
            </div>
          </v-card>

          <div class="section-header mb-2">
            📅 Docket ที่ใกล้ครบกำหนด ({{ dueWithinDays }} วัน)
          </div>
          <v-card outlined class="pa-3">
            <div
              v-if="upcomingDockets.length === 0"
              class="caption grey--text pa-2"
            >
              ไม่มี Docket ที่ใกล้ครบกำหนดใน {{ dueWithinDays }} วันข้างหน้า
            </div>
            <div
              v-for="d in upcomingDockets"
              :key="d.id"
              class="d-flex align-center justify-space-between agency-row"
              style="cursor:pointer;"
              @click="goToDocket(d.id)"
            >
              <div>
                <span class="matter-link font-weight-bold">{{
                  d.client_name
                }}</span>
                <span class="caption grey--text ml-2">{{
                  d.inquiry_topic
                }}</span>
              </div>
              <v-chip x-small :color="upcomingChipColor(d)" label>
                {{ formatDateShort(d.next_due_date) }}
              </v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { server } from "@/services/constants";
import { getIncorporateOverview } from "@/services/api-dtime/api_incorporate";

export default {
  name: "incorporate-overview",
  data() {
    return {
      loading: true,
      dueWithinDays: 30,

      // ⭐ ค่าจาก backend (GET /incorporate/incorporate-docket-overview)
      stats: {
        overdue: 0,
        due_soon: 0,
        in_progress: 0,
        request_payment: 0,
        complete: 0,
        cancelled: 0,
        total: 0,
        active_matters: 0,
      },
      byDocCategory: [],
      byAgency: [],
      urgentDockets: [],
    };
  },
  computed: {
    todayLabel() {
      try {
        return new Date().toLocaleDateString("th-TH", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } catch {
        return "";
      }
    },

    // จำนวนรายการรอดำเนินการ = ทุกอย่างที่ยังไม่ Complete/Cancelled
    pendingTotal() {
      return (
        this.stats.overdue +
        this.stats.due_soon +
        Math.max(
          this.stats.in_progress - this.stats.overdue - this.stats.due_soon,
          0,
        ) +
        this.stats.request_payment
      );
    },

    statCards() {
      return [
        {
          key: "overdue",
          emoji: "🔴",
          label: "Overdue",
          value: this.stats.overdue,
          sub: "เกินกำหนดยื่น",
          color: "#dc2626",
          route: "/incorporate-docket-list",
        },
        {
          key: "due_soon",
          emoji: "🟡",
          label: "Due ≤ 7 วัน",
          value: this.stats.due_soon,
          sub: "ใกล้ครบกำหนด",
          color: "#d97706",
          route: "/incorporate-docket-list",
        },
        {
          key: "in_progress",
          emoji: "🔵",
          label: "In Progress",
          value: this.stats.in_progress,
          sub: "กำลังดำเนินการ",
          color: "#2563eb",
          route: "/incorporate-docket-list",
        },
        {
          key: "request_payment",
          emoji: "💗",
          label: "Request Payment",
          value: this.stats.request_payment,
          sub: "รอเรียกเก็บเงิน",
          color: "#db2777",
          route: "/incorporate-docket-list",
        },
        {
          key: "complete",
          emoji: "🟢",
          label: "Complete",
          value: this.stats.complete,
          sub: "เสร็จสิ้น",
          color: "#16a34a",
          route: "/incorporate-docket-list",
        },
        {
          key: "active_matters",
          emoji: "⚪",
          label: "Active Matters",
          value: this.stats.active_matters,
          sub: "แฟ้มที่ Active",
          color: "#6b7280",
          route: "/incorporate-matter-list",
        },
      ];
    },

    // รายการใกล้ครบกำหนด (ไม่รวม Request Payment เพราะแสดงในฝั่ง Urgent แล้ว)
    // ใช้สำหรับ block "Docket ที่ใกล้ครบกำหนด" ด้านขวา (เรียงตามวันที่ใกล้สุด)
    upcomingDockets() {
      return this.urgentDockets
        .filter((d) => d.status !== "Request Payment" && d.next_due_date)
        .slice()
        .sort((a, b) => new Date(a.next_due_date) - new Date(b.next_due_date))
        .slice(0, 5);
    },

    maxAgencyTotal() {
      if (!this.byAgency.length) return 1;
      return Math.max(...this.byAgency.map((r) => parseInt(r.total) || 0), 1);
    },
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    this.$hideLoader && this.$hideLoader();
    await this.loadOverview();
  },
  methods: {
    async loadOverview() {
      this.loading = true;
      try {
        const company_id = localStorage.getItem(server.COMPANYID);
        const res = await getIncorporateOverview(
          company_id,
          this.dueWithinDays,
        );
        const data = res.data || {};
        this.stats = { ...this.stats, ...(data.stats || {}) };
        this.byDocCategory = data.by_doc_category || [];
        this.byAgency = data.by_agency || [];
        this.urgentDockets = data.urgent_dockets || [];
      } catch (error) {
        console.error("Error loading incorporate overview:", error);
      } finally {
        this.loading = false;
      }
    },

    goToDocket(id) {
      this.$router.push(`/incorporate-docket-view/${id}`);
    },

    daysUntil(dateStr) {
      if (!dateStr) return null;
      const due = new Date(dateStr);
      const today = new Date();
      due.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return Math.round((due - today) / (1000 * 60 * 60 * 24));
    },

    isOverdue(d) {
      if (d.status === "Request Payment") return false;
      const days = this.daysUntil(d.next_due_date);
      return days !== null && days < 0;
    },

    urgentIcon(d) {
      if (d.status === "Request Payment") return "🧾";
      if (this.isOverdue(d)) return "⏰";
      return "📄";
    },

    urgentCardClass(d) {
      if (d.status === "Request Payment") return "card-pink";
      if (this.isOverdue(d)) return "card-red";
      const days = this.daysUntil(d.next_due_date);
      if (days !== null && days <= 7) return "card-yellow";
      return "card-blue";
    },

    urgentTagColor(d) {
      if (d.status === "Request Payment") return "pink lighten-4";
      if (this.isOverdue(d)) return "red lighten-4";
      return "amber lighten-4";
    },

    urgentTagLabel(d) {
      if (d.status === "Request Payment") return "REQ. PAYMENT";
      if (this.isOverdue(d)) return "OVERDUE";
      const days = this.daysUntil(d.next_due_date);
      return days !== null ? `${days} วัน` : "-";
    },

    urgentDaysColor(d) {
      if (d.status === "Request Payment") return "#db2777";
      if (this.isOverdue(d)) return "#dc2626";
      const days = this.daysUntil(d.next_due_date);
      if (days !== null && days <= 7) return "#d97706";
      return "#2563eb";
    },

    urgentDaysLabel(d) {
      if (d.status === "Request Payment") return "฿";
      const days = this.daysUntil(d.next_due_date);
      if (days === null) return "-";
      return days < 0 ? `${days}d` : `${days}d`;
    },

    upcomingChipColor(d) {
      const days = this.daysUntil(d.next_due_date);
      if (days === null) return "grey lighten-3";
      if (days < 0) return "red lighten-4";
      if (days <= 7) return "amber lighten-4";
      return "green lighten-4";
    },

    agencyBarWidth(row) {
      const total = parseInt(row.total) || 0;
      return Math.round((total / this.maxAgencyTotal) * 100);
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        return new Date(dateStr).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      } catch {
        return dateStr;
      }
    },

    formatDateShort(dateStr) {
      if (!dateStr) return "-";
      try {
        return new Date(dateStr).toLocaleDateString("th-TH", {
          month: "2-digit",
          day: "2-digit",
        });
      } catch {
        return dateStr;
      }
    },
  },
};
</script>

<style scoped>
.section-header {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  border-left: 4px solid #0a7ea5;
  padding: 8px 12px;
  background: #f9fafb;
}

.stat-card {
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.15s;
}
.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.urgent-card {
  cursor: pointer;
  border-radius: 8px;
}
.urgent-card.card-red {
  background: #fff1f2;
  border-color: #fca5a5 !important;
}
.urgent-card.card-yellow {
  background: #fffbeb;
  border-color: #fde68a !important;
}
.urgent-card.card-blue {
  background: #eff6ff;
  border-color: #bfdbfe !important;
}
.urgent-card.card-pink {
  background: #fdf2f8;
  border-color: #fbcfe8 !important;
}

.urgent-icon {
  font-size: 18px;
  margin-top: 1px;
}

.matter-link {
  font-size: 12.5px;
  color: #1d4ed8;
}

.agency-row {
  font-size: 12px;
  padding: 7px 0;
  border-bottom: 1px solid #f3f4f6;
}
.agency-row:last-child {
  border-bottom: none;
}

.mini-bar {
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  overflow: hidden;
  width: 90px;
}
.mini-bar > span {
  display: block;
  height: 100%;
  background: #d97706;
}
</style>
